"use client";

import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { usePowerspotResolver, type PowerspotVariantKey, type ResolverResult } from "./resolver";
/* eslint-disable boundaries/dependencies */
import { useAccountUsage } from "@/features/dashboard/queries";
import { SubscriptionPlanId, SubscriptionStatus } from "@/features/subscription/types";
/* eslint-enable boundaries/dependencies */

export type HeroOverlay =
  | "trial_expired"
  | "payment_failed"
  | null;

export interface ConversionSurfaceState {
  topbarVariant: PowerspotVariantKey | null;
  topbar: ResolverResult;
  heroOverlay: HeroOverlay;
}

const DASHBOARD_EXACT_PATHS = new Set(["/", "/home"]);

function isDashboardRoute(pathname: string): boolean {
  return DASHBOARD_EXACT_PATHS.has(pathname) || pathname.startsWith("/home/");
}

/**
 * Single source of truth for which surface owns which conversion state.
 *
 * - Resolver stays route-blind. This arbiter post-processes its output for
 *   route-aware suppression (e.g. trial_expired collapses into the dashboard
 *   hero overlay; pill stays empty on /dashboard to avoid duplicate
 *   "Choose a plan" CTAs).
 * - Dashboard reads `heroOverlay` to render the takeover.
 *
 * Consumers: powerspot-pill, dashboard-page.
 */
export function useConversionSurfaceState(): ConversionSurfaceState {
  const { pathname } = useLocation();
  const onDashboard = isDashboardRoute(pathname);
  const resolver = usePowerspotResolver();
  const { data: usage } = useAccountUsage();

  return useMemo<ConversionSurfaceState>(() => {
    const planId = usage?.planId;
    const isTrial = planId === SubscriptionPlanId.Speak_Free_Trial;
    const isTrialExpired = isTrial && !!usage?.isExpired;
    const isPaymentFailed =
      !isTrial && usage?.status === SubscriptionStatus.PendingPayment;

    let heroOverlay: HeroOverlay = null;
    if (onDashboard) {
      if (isPaymentFailed) heroOverlay = "payment_failed";
      else if (isTrialExpired) heroOverlay = "trial_expired";
    }

    // Suppress pill on /dashboard when the hero overlay owns the surface, and
    // for variants whose dashboard equivalent is the hero (trial_expired,
    // payment_failed). Activation is also suppressed here because the
    // dashboard's ActivationChecklist owns that surface; the pill fills in on
    // every other route.
    let topbarVariant: PowerspotVariantKey | null = resolver.variant;
    if (onDashboard) {
      if (heroOverlay !== null) topbarVariant = null;
      if (
        topbarVariant === "trial_expired" ||
        topbarVariant === "payment_failed" ||
        topbarVariant === "activation"
      ) {
        topbarVariant = null;
      }
    }

    return {
      topbarVariant,
      topbar:
        topbarVariant === resolver.variant
          ? resolver
          : { ...resolver, variant: topbarVariant },
      heroOverlay,
    };
  }, [onDashboard, resolver, usage?.planId, usage?.isExpired, usage?.status]);
}
